import axios from 'axios';
import authService from './AuthService';

/**
 * BidsService - Handles bid operations with SplTrader API
 */
class BidsService {
  constructor() {
    // Verwende direkte URL - CORS muss server-seitig aktiviert werden
    this.baseURL = 'https://www.spltrader.com/api';
    
    // Client-side cache für User Settings (Workaround für API-Bug)
    this.settingsCache = new Map();
      
    this.apiClient = axios.create({
      baseURL: this.baseURL,
      timeout: 8000, // Reduced from 10s to 8s for faster feedback
      headers: {
        'Content-Type': 'application/json'
      },
      // Wichtig: Automatisch Redirects folgen und debugging aktivieren
      maxRedirects: 5,
      validateStatus: function (status) {
        return status >= 200 && status < 400; // Akzeptiere auch 3xx Redirects
      }
    });

    // **PERFORMANCE OPTIMIZATION**: Add caching and request deduplication
    this.cache = new Map();
    this.ongoingRequests = new Map();
    this.CACHE_DURATION = 2 * 60 * 1000; // 2 minutes cache for bids data
    this.lastCacheCleanup = Date.now();
    this.CLEANUP_INTERVAL = 5 * 60 * 1000; // Clean cache every 5 minutes

    // Request Interceptor
    this.apiClient.interceptors.request.use(
      (config) => {
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response Interceptor
    this.apiClient.interceptors.response.use(
      (response) => {
        return response;
      },
      (error) => {
        return Promise.reject(error);
      }
    );
  }

  /**
   * Get authentication token with enhanced handling for logged-in users
   * @param {string} username - The username to get token for
   * @returns {Promise<string>} The authentication token
   */
  async getAuthToken(username) {
    const currentUser = authService.getCurrentUser();
    const actualUsername = username || currentUser?.username;
    
    if (!actualUsername) {
      throw new Error('No username available');
    }
    
    // Check if user is logged in - if not, don't even try to get a token
    if (!authService.isLoggedIn()) {
      throw new Error('User not authenticated - please log in first');
    }
    
    // Use AuthService's existing token management system
    // This prevents unnecessary re-authentication for already logged-in users
    try {
      const token = await authService.getApiToken();
      return token;
    } catch (error) {
      // Enhanced error handling for logged-in users
      if (authService.isLoggedIn()) {
        // User is logged in but token generation failed
        // This could be due to Keychain issues or token expiry
        if (error.message.includes('Keychain') || error.message.includes('signAccountName')) {
          throw new Error('Failed to refresh authentication token - please check your Hive Keychain extension');
        } else if (error.message.includes('token') || error.message.includes('expired')) {
          throw new Error('Authentication token expired - attempting to refresh...');
        } else {
          throw new Error(`Token generation failed: ${error.message}`);
        }
      }
      
      // For test users or fallback scenarios
      if (actualUsername === 'cpzitrone' && !currentUser?.authType) {
        return 'test_token_' + Date.now();
      }
      
      throw error;
    }
  }

  /**
   * Clear the authentication token cache
   * Should be called when user logs out or changes
   */
  clearAuthCache() {
    // Clear AuthService cache instead of local cache
    authService.clearApiTokenCache();
  }

  /**
   * **PERFORMANCE OPTIMIZATION**: Clean expired cache entries
   * @private
   */
  cleanExpiredCache() {
    const now = Date.now();
    
    // Only clean if enough time has passed
    if (now - this.lastCacheCleanup < this.CLEANUP_INTERVAL) {
      return;
    }
    
    for (const [key, entry] of this.cache.entries()) {
      if (now - entry.timestamp > this.CACHE_DURATION) {
        this.cache.delete(key);
      }
    }
    
    this.lastCacheCleanup = now;
  }

  /**
   * **PERFORMANCE OPTIMIZATION**: Get cached data if available and fresh
   * @param {string} key - Cache key
   * @returns {any|null} Cached data or null if not found/expired
   */
  getCachedData(key) {
    this.cleanExpiredCache();
    
    const entry = this.cache.get(key);
    if (!entry) return null;
    
    const isExpired = Date.now() - entry.timestamp > this.CACHE_DURATION;
    if (isExpired) {
      this.cache.delete(key);
      return null;
    }
    
    return entry.data;
  }

  /**
   * **PERFORMANCE OPTIMIZATION**: Set cached data
   * @param {string} key - Cache key
   * @param {any} data - Data to cache
   */
  setCachedData(key, data) {
    this.cache.set(key, {
      data: data,
      timestamp: Date.now()
    });
  }

  /**
   * **PERFORMANCE OPTIMIZATION**: Clear all cached data
   */
  clearCache() {
    this.cache.clear();
    this.ongoingRequests.clear();
  }

  /**
   * Test SplTrader API accessibility with a simple ping
   * @returns {Promise<Object>} Test result
   */
  async testSplTraderAPI() {
    try {
      // Try a simple request to check if the API is responding
      const response = await axios.get('https://www.spltrader.com/api/ping', {
        timeout: 10000,
        validateStatus: function (status) {
          return status >= 200 && status < 500; // Accept even error responses as long as we get a response
        }
      });
      
      return {
        success: true,
        status: response.status,
        message: `API responding with status ${response.status}`
      };
    } catch (error) {
      
      if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
        return {
          success: false,
          error: 'timeout',
          message: 'API timeout - server not responding'
        };
      } else if (error.code === 'ERR_NETWORK') {
        return {
          success: false,
          error: 'network',
          message: 'Network error - check connection or CORS'
        };
      } else {
        return {
          success: false,
          error: 'unknown',
          message: error.message
        };
      }
    }
  }

  /**
   * **PERFORMANCE OPTIMIZED**: Get all bids for a user with caching and request deduplication
   * @param {string} account - The account name (optional, uses current user if not provided)
   * @returns {Promise<Array>} Array of bids
   */
  async getBids(account = null) {
    const currentUser = authService.getCurrentUser();
    const username = account || currentUser?.username;
    
    if (!username) {
      return this.getMockBids();
    }

    // **PERFORMANCE**: Check cache first
    const cacheKey = `bids_${username}`;
    const cachedData = this.getCachedData(cacheKey);
    if (cachedData) {
      return cachedData;
    }

    // **PERFORMANCE**: Check if request is already ongoing
    if (this.ongoingRequests.has(cacheKey)) {
      return this.ongoingRequests.get(cacheKey);
    }

    // **PERFORMANCE**: Create new request with deduplication
    const requestPromise = this._fetchBidsFromAPI(username).then(data => {
      // Cache the results
      this.setCachedData(cacheKey, data);
      // Remove from ongoing requests
      this.ongoingRequests.delete(cacheKey);
      return data;
    }).catch(error => {
      // Remove from ongoing requests on error
      this.ongoingRequests.delete(cacheKey);
      throw error;
    });

    // Store ongoing request
    this.ongoingRequests.set(cacheKey, requestPromise);
    
    return requestPromise;
  }

  /**
   * **PERFORMANCE HELPER**: Internal method to fetch bids from API
   * @private
   * @param {string} username - Username to fetch bids for
   * @returns {Promise<Array>} Array of bids
   */
  async _fetchBidsFromAPI(username) {
    // First check if user has authority before attempting any API calls
    try {
      // Try to get authentication token first - this will fail early if user is not authenticated
      let token = await this.getAuthToken(username);
      
      let response = await this.apiClient.get('/bids', {
        params: {
          account: username,
          token: token
        }
      });

      // Check if the response contains an "Invalid token" error and retry once
      // Only check if response.data is an object with an error property (not an array)
      if (response.data && 
          typeof response.data === 'object' && 
          !Array.isArray(response.data) && 
          response.data.error === 'Invalid token') {
        
        // Clear the cached token and get a fresh one
        authService.clearApiTokenCache();
        token = await this.getAuthToken(username);
        
        // Retry the request with the fresh token
        response = await this.apiClient.get('/bids', {
          params: {
            account: username,
            token: token
          }
        });
      }

      // Check for other errors after retry (but not for successful array responses)
      if (response.data && 
          typeof response.data === 'object' && 
          !Array.isArray(response.data) && 
          response.data.error) {
        throw new Error(`SplTrader API Error: ${response.data.error}`);
      }

      return response.data || [];
    } catch (error) {
      // Enhanced error handling - differentiate between authentication failures
      // and other types of errors, especially for users who are already logged in
      
      // Priority 1: Handle Keychain/authentication cancellation
      if (error.message && (
        error.message.includes('cancelled') || 
        error.message.includes('denied') ||
        error.message.includes('abgebrochen') || 
        error.message.includes('verweigert') ||
        error.message.includes('ignored')
      ) && !error.response) {
        // User cancelled authentication or authentication failed
        authService.clearApiTokenCache();
        
        // For logged-in users, this might be a token refresh issue
        if (authService.isLoggedIn()) {
          throw new Error('Token refresh needed - please wait while we refresh your authentication');
        } else {
          throw new Error('Authentication required to access bids. Please authenticate with your Hive account first.');
        }
      }
      
      // Priority 2: Handle invalid token errors for logged-in users
      if (error.message && error.message.includes('Invalid token') && authService.isLoggedIn()) {
        authService.clearApiTokenCache();
        throw new Error('Session expired - refreshing authentication token...');
      }
      
      // Priority 3: Handle network/CORS errors
      if (error.code === 'ERR_NETWORK' || 
          (error.response && error.response.status === 0) ||
          error.message.includes('CORS')) {
        throw new Error('CORS Error: Please install a CORS browser extension or enable CORS in the SplTrader API.');
      }
      
      // Priority 4: Handle API response errors
      if (error.response && error.response.status >= 400) {
        throw new Error(`SplTrader API Error: ${error.response.status} - ${error.response.statusText || error.message}`);
      }
      
      // Priority 5: All other errors
      throw new Error(`Unknown error: ${error.message}`);
    }
  }

  /**
   * **PERFORMANCE OPTIMIZED**: Alternative getBids using fetch instead of axios for better CORS handling
   * @param {string} account - The account name (optional, uses current user if not provided)
   * @returns {Promise<Array>} Array of bids
   */
  async getBidsWithFetch(account = null) {
    const currentUser = authService.getCurrentUser();
    const username = account || currentUser?.username;
    
    if (!username) {
      return this.getMockBids();
    }

    // **PERFORMANCE**: Check cache first
    const cacheKey = `bids_fetch_${username}`;
    const cachedData = this.getCachedData(cacheKey);
    if (cachedData) {
      return cachedData;
    }

    // **PERFORMANCE**: Check if request is already ongoing
    if (this.ongoingRequests.has(cacheKey)) {
      return this.ongoingRequests.get(cacheKey);
    }

    // **PERFORMANCE**: Create new request with deduplication
    const requestPromise = this._fetchBidsWithFetchFromAPI(username).then(data => {
      // Cache the results
      this.setCachedData(cacheKey, data);
      // Remove from ongoing requests
      this.ongoingRequests.delete(cacheKey);
      return data;
    }).catch(error => {
      // Remove from ongoing requests on error
      this.ongoingRequests.delete(cacheKey);
      throw error;
    });

    // Store ongoing request
    this.ongoingRequests.set(cacheKey, requestPromise);
    
    return requestPromise;
  }

  /**
   * **PERFORMANCE HELPER**: Internal method to fetch bids using fetch API
   * @private
   * @param {string} username - Username to fetch bids for
   * @returns {Promise<Array>} Array of bids
   */
  async _fetchBidsWithFetchFromAPI(username) {
    try {
      // Get cached token to prevent multiple authentication prompts
      let token = await this.getAuthToken(username);
      
      const url = `${this.baseURL}/bids?account=${username}&token=${token}`;
      
      // **PERFORMANCE**: Use shorter timeout for fetch
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 6000); // 6 seconds timeout
      
      try {
        const response = await fetch(url, {
          method: 'GET',
          mode: 'cors',
          headers: {
            'Accept': 'application/json',
          },
          signal: controller.signal
        });

        clearTimeout(timeoutId);

        if (!response.ok) {
          // If it's an auth error, retry with fresh token
          if (response.status === 401 || response.status === 403) {
            authService.clearApiTokenCache();
            token = await this.getAuthToken(username);
            
            const retryUrl = `${this.baseURL}/bids?account=${username}&token=${token}`;
            const retryResponse = await fetch(retryUrl, {
              method: 'GET',
              mode: 'cors',
              headers: {
                'Accept': 'application/json',
              }
            });
            
            if (!retryResponse.ok) {
              throw new Error(`SplTrader API Error: ${retryResponse.status} - ${retryResponse.statusText}`);
            }
            
            const retryData = await retryResponse.json();
            return retryData || [];
          }
          
          throw new Error(`SplTrader API Error: ${response.status} - ${response.statusText}`);
        }

        const data = await response.json();
        return data || [];
      } finally {
        clearTimeout(timeoutId);
      }
    } catch (error) {
      
      // Priority 1: Handle authentication errors first
      if (error.message && (
        error.message.includes('cancelled') || 
        error.message.includes('denied') ||
        error.message.includes('abgebrochen') || 
        error.message.includes('verweigert') ||
        error.message.includes('ignored')
      ) && !error.response) {
        // User cancelled authentication
        authService.clearApiTokenCache();
        throw new Error('Authentication required to access bids. Please authenticate with your Hive account first.');
      }
      
      // Priority 2: Handle timeout errors
      if (error.name === 'AbortError') {
        throw new Error('API request was cancelled after 6 seconds. Server is not responding.');
      } 
      
      // Priority 3: Handle network errors
      if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
        throw new Error('Network error or CORS problem. Bids could not be loaded.');
      }
      
      // Priority 4: All other errors
      throw error;
    }
  }

  /**
   * **PERFORMANCE OPTIMIZED**: Create or update a bid with cache invalidation
   * @param {Object} bidData - The bid data
   * @param {string} bidData.id - Card ID (e.g. "555", "G789" for gold, "B789" for black)
   * @param {number} bidData.max_price_bcx - Maximum price per BCX
   * @param {number} bidData.max_card_bcx - Maximum BCX per card
   * @param {number} bidData.total_bcx - Total BCX wanted
   * @param {boolean} bidData.active - Whether the bid is active
   * @param {boolean} bidData.use_pm_bid_price - Whether to use PM bid price
   * @returns {Promise<Object>} The created/updated bid
   */
  async createOrUpdateBid(bidData) {
    try {
      const currentUser = authService.getCurrentUser();
      
      if (!currentUser?.username) {
        throw new Error('Benutzer ist nicht angemeldet');
      }

      // **PERFORMANCE**: Reduce timeout for better user feedback
      const originalTimeout = this.apiClient.defaults.timeout;
      this.apiClient.defaults.timeout = 6000; // 6 seconds for write operations

      try {
        // Get cached token to prevent multiple authentication prompts
        let token = await this.getAuthToken(currentUser.username);

        // Validate required fields
        if (!bidData.id) {
          throw new Error('Card ID is required');
        }
        if (!bidData.max_price_bcx || bidData.max_price_bcx <= 0) {
          throw new Error('Valid price per BCX is required');
        }
        if (!bidData.total_bcx || bidData.total_bcx <= 0) {
          throw new Error('Valid total BCX is required');
        }

        // SplTrader API expects nested "bid" object structure
        const createPayload = () => ({
          account: currentUser.username,
          token: token,
          bid: {
            id: bidData.id,
            max_price_bcx: Number(bidData.max_price_bcx),
            max_card_bcx: Number(bidData.max_card_bcx || 1),
            total_bcx: Number(bidData.total_bcx),
            active: Boolean(bidData.active !== false), // Default to true
            use_pm_bid_price: Boolean(bidData.use_pm_bid_price || false) // Default to false
          }
        });

        let payload = createPayload();

        let response = await this.apiClient.post('/bids', payload);

        // Check if the response contains an "Invalid token" error and retry once
        if (response.data && typeof response.data === 'object' && response.data.error === 'Invalid token') {
          
          // Clear the cached token and get a fresh one
          authService.clearApiTokenCache();
          token = await this.getAuthToken(currentUser.username);
          
          // Retry the request with the fresh token
          payload = createPayload();
          response = await this.apiClient.post('/bids', payload);
        }

        // **PERFORMANCE**: Invalidate cache after successful operation
        this._invalidateBidsCache(currentUser.username);

        return response.data;
      } finally {
        // Restore original timeout
        this.apiClient.defaults.timeout = originalTimeout;
      }
    } catch (error) {
      
      // Handle specific error cases
      if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
        // Timeout error
        throw new Error('SplTrader API is not responding. Timeout after 6 seconds. Please try again later.');
      } else if (error.message && (
        error.message.includes('abgebrochen') || 
        error.message.includes('verweigert') ||
        error.message.includes('ignored') ||
        error.message.includes('cancelled') ||
        error.message.includes('denied')
      ) && !error.response) {
        // User cancelled authentication - clear cache
        authService.clearApiTokenCache();
        throw new Error('Authentication was cancelled. Bid could not be saved.');
      } else if (error.code === 'ERR_NETWORK' || 
                 (error.response && error.response.status === 0) ||
                 error.message.includes('CORS')) {
        // Network/CORS error
        throw new Error('Network error or CORS problem. Bid could not be saved.');
      } else if (error.response && error.response.status >= 400) {
        // API error with response
        const errorMsg = error.response.data?.message || error.response.statusText || 'Unknown API error';
        throw new Error(`SplTrader API Error (${error.response.status}): ${errorMsg}`);
      }
      
      // For other errors, provide generic message
      throw new Error(`Error saving bid: ${error.message}`);
    }
  }

  /**
   * **PERFORMANCE OPTIMIZED**: Delete a bid with cache invalidation
   * @param {string} bidId - The bid ID to delete
   * @returns {Promise<boolean>} Success status
   */
  async deleteBid(bidId) {
    try {
      const currentUser = authService.getCurrentUser();
      
      if (!currentUser?.username) {
        throw new Error('User is not logged in');
      }

      // **PERFORMANCE**: Reduce timeout for better user feedback
      const originalTimeout = this.apiClient.defaults.timeout;
      this.apiClient.defaults.timeout = 6000; // 6 seconds for delete operations

      try {
        // Get cached token to prevent multiple authentication prompts
        let token = await this.getAuthToken(currentUser.username);

        const createPayload = () => ({
          account: currentUser.username,
          token: token,
          id: bidId
        });

        let payload = createPayload();

        let response = await this.apiClient.delete('/bids', { data: payload });

        // Check if the response contains an "Invalid token" error and retry once
        if (response.data && typeof response.data === 'object' && response.data.error === 'Invalid token') {
          
          // Clear the cached token and get a fresh one
          authService.clearApiTokenCache();
          token = await this.getAuthToken(currentUser.username);
          
          // Retry the request with the fresh token
          payload = createPayload();
          response = await this.apiClient.delete('/bids', { data: payload });
        }

        // **PERFORMANCE**: Invalidate cache after successful operation
        this._invalidateBidsCache(currentUser.username);

        return true;
      } finally {
        // Restore original timeout
        this.apiClient.defaults.timeout = originalTimeout;
      }
    } catch (error) {
      
      // Handle specific error cases
      if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
        throw new Error('SplTrader API is not responding. Timeout after 6 seconds. Please try again later.');
      } else if (error.message && (
        error.message.includes('abgebrochen') || 
        error.message.includes('verweigert') ||
        error.message.includes('ignored') ||
        error.message.includes('cancelled') ||
        error.message.includes('denied')
      )) {
        // User cancelled authentication - clear cache
        authService.clearApiTokenCache();
        throw new Error('Authentication was cancelled. Bid could not be deleted.');
      } else if (error.code === 'ERR_NETWORK' || 
                 (error.response && error.response.status === 0) ||
                 error.message.includes('CORS')) {
        // Network/CORS error
        throw new Error('Network error. Bid could not be deleted. Please try again later.');
      } else if (error.response && error.response.status >= 500) {
        // Server error
        throw new Error('Server error. Bid could not be deleted. Please try again later.');
      }
      
      // For other errors, provide generic message
      throw new Error('Error deleting bid. Please try again later.');
    }
  }

  /**
   * **PERFORMANCE HELPER**: Invalidate cache for a specific user
   * @private
   * @param {string} username - Username to invalidate cache for
   */
  _invalidateBidsCache(username) {
    const cacheKeys = [`bids_${username}`, `bids_fetch_${username}`];
    
    for (const key of cacheKeys) {
      this.cache.delete(key);
      this.ongoingRequests.delete(key);
    }
  }

  /**
   * Get user settings from SplTrader API using fetch for consistency
   * @param {string} account - The account name (optional, uses current user if not provided)
   * @returns {Promise<Object>} User settings object with currency and minimumBalance
   */
  async getUserSettings(account = null) {
    const currentUser = authService.getCurrentUser();
    const username = account || currentUser?.username;
    
    if (!username) {
      throw new Error('No username available');
    }

    // Check cache first
    if (this.settingsCache.has(username)) {
      return this.settingsCache.get(username);
    }

    try {
      // Get cached token to prevent multiple authentication prompts
      let token = await this.getAuthToken(username);
      
      // Use fetch for consistency with updateUserSettings
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 8000); // 8 seconds timeout

      try {
        let response = await fetch(`${this.baseURL}/settings?account=${username}&token=${token}`, {
          method: 'GET',
          mode: 'cors',
          headers: {
            'Accept': 'application/json',
          },
          signal: controller.signal
        });

        clearTimeout(timeoutId);

        if (!response.ok) {
          // If it's an auth error, retry with fresh token
          if (response.status === 401 || response.status === 403) {
            authService.clearApiTokenCache();
            token = await this.getAuthToken(username);
            
            response = await fetch(`${this.baseURL}/settings?account=${username}&token=${token}`, {
              method: 'GET',
              mode: 'cors',
              headers: {
                'Accept': 'application/json',
              }
            });
            
            if (!response.ok) {
              throw new Error(`SplTrader API Fehler: ${response.status} - ${response.statusText}`);
            }
          } else {
            throw new Error(`SplTrader API Fehler: ${response.status} - ${response.statusText}`);
          }
        }

        const data = await response.json();
        
        // Check for API errors in response data
        if (data && typeof data === 'object' && data.error) {
          throw new Error(`SplTrader API Fehler: ${data.error}`);
        }

        // Cache the settings data
        this.settingsCache.set(username, {
          currency: data?.currency || 'DEC',
          minimumBalance: data?.minimumBalance || 0
        });

        // Return settings with defaults if not found
        return {
          currency: data?.currency || 'DEC',
          minimumBalance: data?.minimumBalance || 0
        };
      } finally {
        clearTimeout(timeoutId);
      }
    } catch (error) {
      console.error("Error loading user settings:", error);
      
      // Handle specific error cases
      if (error.name === 'AbortError') {
        throw new Error('API-Anfrage wurde nach 8 Sekunden abgebrochen. Server reagiert nicht.');
      } else if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
        throw new Error('Netzwerkfehler oder CORS-Problem. Einstellungen konnten nicht geladen werden.');
      } else if (error.message && 
          (error.message.includes('abgebrochen') || 
           error.message.includes('verweigert') ||
           error.message.includes('ignored'))) {
        // User cancelled authentication - clear cache
        authService.clearApiTokenCache();
        throw new Error('Signierung wurde vom Benutzer abgebrochen. Bitte versuche es erneut.');
      }
      
      // For other errors, throw with detailed information
      throw new Error(`Unbekannter Fehler: ${error.message}`);
    }
  }

  /**
   * Update user settings in SplTrader API using fetch for better CORS handling
   * @param {Object} settings - Settings object
   * @param {string} settings.currency - "DEC" or "CREDITS"
   * @param {number} settings.minimumBalance - Minimum balance to maintain
   * @param {string} account - The account name (optional, uses current user if not provided)
   * @returns {Promise<Object>} Updated settings
   */
  async updateUserSettings(settings, account = null) {
    const currentUser = authService.getCurrentUser();
    const username = account || currentUser?.username;
    
    if (!username) {
      throw new Error('No username available');
    }

    try {
      // Validate settings
      if (!settings.currency || !['DEC', 'CREDITS'].includes(settings.currency)) {
        throw new Error('Valid currency is required (DEC or CREDITS)');
      }
      if (settings.minimumBalance < 0) {
        throw new Error('Minimum balance must be positive');
      }

      // Get cached token to prevent multiple authentication prompts
      let token = await this.getAuthToken(username);
      const createPayload = () => ({
        account: username,
        token: token,
        settings: {
          currency: settings.currency,
          minimumBalance: Number(settings.minimumBalance || 0)
        }
      });

      let payload = createPayload();
      // Use fetch instead of axios for better redirect handling
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 seconds timeout

      try {
        let response = await fetch(`${this.baseURL}/settings`, {
          method: 'POST',
          mode: 'cors',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
          body: JSON.stringify(payload),
          signal: controller.signal
        });

        clearTimeout(timeoutId);
        if (!response.ok) {
          // If it's an auth error, retry with fresh token
          if (response.status === 401 || response.status === 403) {
            authService.clearApiTokenCache();
            token = await this.getAuthToken(username);
            
            payload = createPayload();
            response = await fetch(`${this.baseURL}/settings`, {
              method: 'POST',
              mode: 'cors',
              headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
              },
              body: JSON.stringify(payload)
            });
            
            if (!response.ok) {
              throw new Error(`SplTrader API Fehler: ${response.status} - ${response.statusText}`);
            }
          } else {
            throw new Error(`SplTrader API Fehler: ${response.status} - ${response.statusText}`);
          }
        }

        const data = await response.json();
        // Check for API errors in response data
        if (data && typeof data === 'object' && data.error) {
          throw new Error(`SplTrader API Fehler: ${data.error}`);
        }

        // Update cache with new settings
        this.settingsCache.set(username, {
          currency: data?.currency || 'DEC',
          minimumBalance: data?.minimumBalance || 0
        });

        return data;
      } finally {
        clearTimeout(timeoutId);
      }
    } catch (error) {
      console.error("Error updating user settings:", error);
      
      // Handle specific error cases
      if (error.name === 'AbortError') {
        throw new Error('API-Anfrage wurde nach 10 Sekunden abgebrochen. Server reagiert nicht.');
      } else if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
        throw new Error('Netzwerkfehler oder CORS-Problem. Einstellungen konnten nicht gespeichert werden.');
      } else if (error.message && (
        error.message.includes('abgebrochen') || 
        error.message.includes('verweigert') ||
        error.message.includes('ignored')
      )) {
        // User cancelled authentication - clear cache
        authService.clearApiTokenCache();
        throw new Error('Signierung wurde abgebrochen. Einstellungen konnten nicht gespeichert werden.');
      }
      
      // For other errors, provide generic message
      throw new Error(`Fehler beim Speichern der Einstellungen: ${error.message}`);
    }
  }

  /**
   * Convert card details to SplTrader card ID format
   * @param {Object} card - Card object with details
   * @param {Object} details - Card details object
   * @returns {string} Formatted card ID (e.g. "555", "G555", "B555")
   */
  formatCardId(card, details) {
    const baseId = String(details?.card_detail_id || details?.id || '0');
    
    if (!card) {
      return baseId;
    }

    // Handle different foil types based on Splinterlands foil system:
    // foil: 0 = Regular
    // foil: 1 = Gold Foil (gold: true)
    // foil: 2 = Gold Arcana (gold: true) 
    // foil: 3 = Black Foil (gold: false)
    // foil: 4 = Black Arcana (gold: false)
    
    const foilType = card.foil !== undefined ? card.foil : 0;
    const isGold = card.gold || false;
    const isArcana = details?.type === "Arcana" || details?.edition === 14;
  
    
    // Handle foil types correctly according to Splinterlands system
    switch (foilType) {
      case 4: // Black Arcana
        return `BA${baseId}`;
      case 3: // Black Foil
        return `B${baseId}`;
      case 2: // Gold Arcana (must also have gold: true)
        return `GA${baseId}`;
      case 1: // Gold Foil (must also have gold: true)
        return `G${baseId}`;
      case 0: // Regular
      default:
        // For legacy support or edge cases, check gold property
        // But this should rarely be used with proper foil values
        if (isGold) {
          if (isArcana) {
            return `GA${baseId}`;
          } else {
            return `G${baseId}`;
          }
        }
        return baseId;
    }
  }

  /**
   * Parse SplTrader card ID to extract base ID and foil type
   * @param {string} cardId - SplTrader card ID (e.g. "555", "G555", "B555")
   * @returns {Object} Parsed card info
   */
  parseCardId(cardId) {
    if (!cardId) return { baseId: '0', foil: 'regular', isGold: false, isArcane: false };

    // Check for Gold Arcane
    if (cardId.startsWith('GA')) {
      return {
        baseId: cardId.substring(2),
        foil: 'gold_arcane',
        isGold: true,
        isArcane: true
      };
    }
    
    // Check for Black Arcane
    if (cardId.startsWith('BA')) {
      return {
        baseId: cardId.substring(2),
        foil: 'black_arcane',
        isGold: false,
        isArcane: true
      };
    }
    
    // Check for Gold
    if (cardId.startsWith('G')) {
      return {
        baseId: cardId.substring(1),
        foil: 'gold',
        isGold: true,
        isArcane: false
      };
    }
    
    // Check for Black
    if (cardId.startsWith('B')) {
      return {
        baseId: cardId.substring(1),
        foil: 'black',
        isGold: false,
        isArcane: false
      };
    }
    
    // Regular
    return {
      baseId: cardId,
      foil: 'regular',
      isGold: false,
      isArcane: false
    };
  }

  /**
   * Test API connection
   * @returns {Promise<boolean>} Connection status
   */
  async testConnection() {
    try {
      const currentUser = authService.getCurrentUser();
      
      if (!currentUser?.username) {
        throw new Error('Benutzer ist nicht angemeldet');
      }

      // Try to get bids as a connection test
      await this.getBids();
      return true;
    } catch (error) {
      return false;
    }
  }

  /**
   * Test different API endpoint configurations
   * @returns {Promise<Object>} Test results
   */
  async testApiEndpoints() {
    const currentUser = authService.getCurrentUser();
    const username = currentUser?.username || 'testuser';
    
    const testConfigs = [
      { 
        url: 'https://spltrader.com/api/bids',
        method: 'GET',
        params: { account: username, token: 'test_token' },
        description: 'Current configuration'
      },
      { 
        url: 'https://spltrader.com/api/bids/',
        method: 'GET', 
        params: { account: username, token: 'test_token' },
        description: 'With trailing slash'
      },
      { 
        url: 'https://www.spltrader.com/api/bids',
        method: 'GET',
        params: { account: username, token: 'test_token' },
        description: 'With www subdomain'
      },
      { 
        url: 'http://spltrader.com/api/bids',
        method: 'GET',
        params: { account: username, token: 'test_token' },
        description: 'HTTP instead of HTTPS'
      }
    ];

    const results = [];

    for (const config of testConfigs) {
      try {
        const response = await axios({
          method: config.method,
          url: config.url,
          params: config.params,
          timeout: 5000,
          maxRedirects: 5,
          validateStatus: (status) => status < 500 // Accept redirects and client errors
        });

        results.push({
          config: config.description,
          url: config.url,
          status: response.status,
          statusText: response.statusText,
          success: true,
          data: response.data
        });
        
      } catch (error) {
        results.push({
          config: config.description,
          url: config.url,
          status: error.response?.status,
          statusText: error.response?.statusText,
          success: false,
          error: error.message,
          redirected: error.response?.request?.responseURL !== config.url
        });
      }
    }

    return results;
  }

  /**
   * Convert legacy bid data to SplTrader format
   * @param {Object} legacyBid - Bid in our internal format
   * @param {Object} card - Card object
   * @param {Object} details - Card details
   * @returns {Object} Bid in SplTrader format
   */
  convertToSplTraderFormat(legacyBid, card, details) {
    return {
      id: this.formatCardId(card, details),
      max_price_bcx: legacyBid.pricePerBcx || legacyBid.pmBidPrice / legacyBid.totalBcx,
      max_card_bcx: legacyBid.maxBcxCard || 1,
      total_bcx: legacyBid.totalBcx || 1,
      active: legacyBid.active !== false,
      use_pm_bid_price: legacyBid.use_pm_bid_price || false
    };
  }

  /**
   * Convert SplTrader bid data to our internal format
   * @param {Object} splTraderBid - Bid from SplTrader API
   * @param {Object} cardDetails - Card details for display
   * @returns {Object} Bid in our internal format
   */
  convertFromSplTraderFormat(splTraderBid, cardDetails = null) {
    const parsedCard = this.parseCardId(splTraderBid.id);
    
    return {
      id: splTraderBid.id, // Use SplTrader ID as our ID
      user: authService.getCurrentUser()?.username || 'unknown',
      cardName: cardDetails?.name || `Card ${parsedCard.baseId}`,
      cardId: parsedCard.baseId,
      // Use only the actual API field names from response
      maxPriceBcx: Number(splTraderBid.max_price_bcx || 0),
      totalBcx: Number(splTraderBid.total_bcx || 0),
      originalTotalBcx: Number(splTraderBid.original_total_bcx || splTraderBid.total_bcx || 0),
      maxBcxCard: Number(splTraderBid.max_card_bcx || 1),
      active: Boolean(splTraderBid.active),
      use_pm_bid_price: Boolean(splTraderBid.use_pm_bid_price || false),
      // Parsed card info for display
      foilType: parsedCard.foil,
      isGold: parsedCard.isGold,
      isArcane: parsedCard.isArcane,
      // Add parsed card data for image generation
      parsedCard: parsedCard
    };
  }

  /**
   * Get mock bids for testing/fallback when API is not available
   * @returns {Array} Array of mock bid objects
   */
  getMockBids() {
    const currentUser = authService.getCurrentUser();
    const username = currentUser?.username || 'testuser';
    
    return [
      {
        id: '555',
        user: username,
        cardName: 'Pelacor Bandit',
        cardId: '555',
        pricePerBcx: 0.02,
        pmBidPrice: 2.0,
        totalBcx: 100,
        maxBcxCard: 1,
        maxPrice: 2.0,
        active: true,
        createdAt: new Date().toISOString(),
        foilType: 'regular',
        isGold: false,
        isArcane: false,
        use_pm_bid_price: false
      },
      {
        id: 'G789',
        user: username,
        cardName: 'Chaos Legion Pack',
        cardId: '789',
        pricePerBcx: 0.05,
        pmBidPrice: 5.0,
        totalBcx: 100,
        maxBcxCard: 2,
        maxPrice: 10.0,
        active: false,
        createdAt: new Date(Date.now() - 86400000).toISOString(), // 1 day ago
        foilType: 'gold',
        isGold: true,
        isArcane: false,
        use_pm_bid_price: true
      },
      {
        id: 'B234',
        user: username,
        cardName: 'Djinn Oshannus',
        cardId: '234',
        pricePerBcx: 0.08,
        pmBidPrice: 8.0,
        totalBcx: 100,
        maxBcxCard: 1,
        maxPrice: 8.0,
        active: true,
        createdAt: new Date(Date.now() - 172800000).toISOString(), // 2 days ago
        foilType: 'black',
        isGold: false,
        isArcane: false,
        use_pm_bid_price: false
      }
    ];
  }

  /**
   * Simple test to verify API connectivity
   * @returns {Promise<boolean>} Success status
   */
  async testSimpleConnection() {
    try {
      const currentUser = authService.getCurrentUser();
      const username = currentUser?.username || 'cpzitrone';
      
      // Use the working URL from the user's test
      const testUrl = `https://www.spltrader.com/api/bids?account=${username}&token=test_token`;
      
      const response = await fetch(testUrl, {
        method: 'GET',
        mode: 'cors',
        headers: {
          'Accept': 'application/json',
        }
      });
      if (response.ok) {
        await response.json(); // Response processed but data not used
        return true;
      } else {
        return false;
      }
    } catch (error) {
      return false;
    }
  }
}

// Export singleton instance
const bidsService = new BidsService();
export default bidsService;
